import React from 'react';
import { useSelector } from 'react-redux';
import { Button, Form, Row, Col } from 'antd';
import { SearchOutlined, CloseOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import * as sharedActions from '../../../shared/store/actions';
import * as actions from '../store/actions';

import Filter from '../../../shared/components/filter';

import { PolicyOriginTypes, getPolicyOriginTypesDescription } from '../../../shared/constants/enums';
import { filterTypes } from '../../../shared/constants/filterType';
import { RootState } from '../../../store';

interface NotesFilterProps {
  fetchFilteredData: (formData: any) => void;
  filterAppliedChanging: (isApplied: boolean) => void;
  filterPanelVisibleChanging: (isVisible: boolean) => void;
  isHistoryNotes: boolean | undefined;
  initialFilterValues?: any;
}

const NotesFilter: React.FC<NotesFilterProps> = ({
  fetchFilteredData,
  filterAppliedChanging,
  filterPanelVisibleChanging,
  isHistoryNotes,
  initialFilterValues,
}) => {
  const [filterForm] = Form.useForm();

  const loading = useSelector((state: RootState) => state.notes.loading);
  const users = useSelector((state: RootState) => state.shared.users);
  const usersLoading = useSelector((state: RootState) => state.shared.usersLoading);
  const noteTypes = useSelector((state: RootState) => state.noteTypes.noteTypes);
  const noteTypesLoading = useSelector((state: RootState) => state.noteTypes.noteTypesLoading);
  const policies = useSelector((state: RootState) => state.noteTypes.policies);
  const policiesLoading = useSelector((state: RootState) => state.noteTypes.policiesLoading);
  const tasks = useSelector((state: RootState) => state.noteTypes.tasks);
  const tasksLoading = useSelector((state: RootState) => state.noteTypes.tasksLoading);
  const claims = useSelector((state: RootState) => state.noteTypes.claims);
  const claimsLoading = useSelector((state: RootState) => state.noteTypes.claimsLoading);
  const insureds = useSelector((state: RootState) => state.noteTypes.insureds);
  const insuredsLoading = useSelector((state: RootState) => state.noteTypes.insuredsLoading);
  const noteDispositions = useSelector((state: RootState) => state.noteTypes.noteDispositions);
  const noteDispositionsLoading = useSelector((state: RootState) => state.noteTypes.noteDispositionsLoading);

  const applyFilters = (): void => {
    const formData = filterForm.getFieldsValue();

    fetchFilteredData(formData);
    filterPanelVisibleChanging(false);
    checkIsFilterApplied();
  };

  const clearFilters = (): void => {
    filterForm.resetFields();
    const formData = filterForm.getFieldsValue();
    fetchFilteredData(formData);
    checkIsFilterApplied();
  };

  const checkIsFilterApplied = (): void => {
    const formData = filterForm.getFieldsValue();
    let isFilterApplied = false;

    for (const [key, value] of Object.entries(formData)) {
      if (value !== undefined && value !== null && (!Array.isArray(value) || (Array.isArray(value) && value.length > 0))) {
        isFilterApplied = true;
        break;
      }
    }

    filterAppliedChanging(isFilterApplied);
  };

  return (
    <Form layout="vertical" size="small" className="header-filter-form" form={filterForm} initialValues={initialFilterValues}>
      <Row gutter={[16, 0]}>
        <Filter
          filterType={filterTypes.rangePicker}
          name={'createDate'}
          label={'Create Date (from-to)'}
          span={12}
          wrapperCol={{ sm: 24 }}
          onChange={(value: any) => {
            const date = value as dayjs.Dayjs[];
            if (date && date.length) {
              for (let i = 0; i < date.length; i++) {
                if (date[i]) {
                  if (i === 0) {
                    date[i] = date[i].utc().startOf('day');
                  }

                  if (i === 1) {
                    date[i] = date[i].utc().endOf('day');
                  }
                }
              }
            }
          }}
        />

        <Filter
          filterType={filterTypes.inputText}
          name={'subject'}
          label={'Subject'}
          span={12}
          wrapperCol={{ sm: 24 }}
        />

        <Filter
          filterType={filterTypes.dropdownWithLocalData}
          name={'notePolicyOrigins'}
          label={'Origin'}
          span={12}
          data={Object.keys(PolicyOriginTypes)
            .filter(key => isNaN(Number(key)))
            .map(key => ({
              key: PolicyOriginTypes[key as keyof typeof PolicyOriginTypes],
              value: getPolicyOriginTypesDescription(PolicyOriginTypes[key as keyof typeof PolicyOriginTypes])
            }))}
          wrapperCol={{ sm: 24 }}
          maxTagCount={1}
        />

        <Filter
          filterType={filterTypes.dropdownWithServerData}
          name={'noteTypeIds'}
          label={'Type'}
          span={12}
          data={noteTypes}
          dataLoading={noteTypesLoading}
          dataSearchAction={actions.getNoteTypes}
          filterByChildData={true}
          wrapperCol={{ sm: 24 }}
          keyPropertyName={'id'}
          textPropertyName={'name'}
          maxTagCount={1}
        />

        <Filter
          filterType={filterTypes.dropdownWithServerData}
          name={'dispositionIds'}
          label={'Disposition'}
          span={12}
          data={noteDispositions ? noteDispositions.dataList : []}
          dataLoading={noteDispositionsLoading}
          dataSearchAction={actions.getNoteDispositions}
          filterByChildData={true}
          wrapperCol={{ sm: 24 }}
          keyPropertyName={'key'}
          textPropertyName={'value'}
          maxTagCount={1}
        />

        <Filter
          filterType={filterTypes.dropdownWithServerData}
          name={'userIds'}
          label={'Added by'}
          span={12}
          data={users}
          dataLoading={usersLoading}
          dataSearchAction={sharedActions.getUsers}
          wrapperCol={{ sm: 24 }}
          keyPropertyName={'id'}
          textPropertyName={(user: { active: boolean; displayName: string }) => {
            return user.active ? user.displayName : '(Inactive) ' + user.displayName;
          }}
          maxTagCount={1}
        />

        <Filter
          filterType={filterTypes.dropdownWithServerData}
          name={'policyIds'}
          label={'Policy'}
          span={12}
          data={policies}
          dataLoading={policiesLoading}
          dataSearchAction={(searchString?: string) => actions.getPolicies(undefined, searchString)}
          wrapperCol={{ sm: 24 }}
          keyPropertyName={'id'}
          textPropertyName={'number'}
          maxTagCount={1}
        />

        {isHistoryNotes ? (
          <>
            <Filter
              filterType={filterTypes.dropdownWithServerData}
              name={'insuredIds'}
              label={'Insured/Prospect'}
              span={12}
              data={insureds?.dataList}
              dataLoading={insuredsLoading}
              dataSearchAction={(searchString?: string) => actions.getInsuredsProspects({ pageSize: 15, pageNumber: 1, searchString })}
              wrapperCol={{ sm: 24 }}
              maxTagCount={1}
            />

            <Filter
              filterType={filterTypes.dropdownWithServerData}
              name={'taskIds'}
              label={'Task'}
              span={12}
              data={tasks}
              dataLoading={tasksLoading}
              dataSearchAction={actions.getTasks}
              wrapperCol={{ sm: 24 }}
              keyPropertyName={'id'}
              textPropertyName={'ticketTitle'}
              maxTagCount={1}
            />

            <Filter
              filterType={filterTypes.dropdownWithServerData}
              name={'claimIds'}
              label={'Claim'}
              span={12}
              data={claims}
              dataLoading={claimsLoading}
              dataSearchAction={actions.getClaims}
              wrapperCol={{ sm: 24 }}
              keyPropertyName={'id'}
              textPropertyName={'claimNumber'}
              maxTagCount={1}
            />
          </>
        ) : null}
      </Row>

      <Row gutter={[8, 0]} justify="end">
        <Col
          span={10}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type="primary"
            ghost
            icon={<SearchOutlined />}
            title="Apply Search"
            onClick={applyFilters}
            style={{ marginRight: '8px' }}
            loading={loading}
          >
            Search
          </Button>

          <Button
            type="primary"
            ghost
            icon={<ClearOutlined />}
            title="Reset Filters"
            onClick={clearFilters}
            style={{ marginRight: '8px' }}
            loading={loading}
          >
            Reset
          </Button>

          <Button type="primary" ghost icon={<CloseOutlined />} title="Close Filter Panel" onClick={() => filterPanelVisibleChanging(false)}>
            Close
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default NotesFilter;
